import { useState, useCallback } from 'react';
import { deduplicated } from '~/util/array';

type SelectAllFunction = (
  selectedItemIds: Array<string>,
  setSelected: boolean,
) => void;

type SelectFunction = (id: string) => void;

export type ReturnProps = {
  itemsSelected: Array<string>;
  allSelected: boolean;
  selectAllItemsOnPage: SelectAllFunction;
  toggleRowSelect: SelectFunction;
  checkIfSelected: (id: string) => boolean;
  selectAllItems: (items: Array<string>) => void;
  deselectAllItems: () => void;
};

/**
 * A hook to manage row selection in a table. It will give the user:
 * - `itemsSelected` - A list of all selected rows
 * - `allSelected` - A boolean value that shows if all rows are selected or not.
 * - `selectAllItemsOnPage` - A function to toggle all rows, if there is at least one row selected it will firstly deselect all and then select all
 * - `toggleRowSelect` - A function to toggle one row selection
 * - `selectAllItems` - A function that toggles selection on all pages
 * - `deselectAllItems` - A function that removes all selection
 *
 * It expects:
 *   - no parameters
 */
const useRowSelect = (): ReturnProps => {
  const [itemsSelected, setItemsSelected] = useState<Array<string>>([]);
  const [allSelected, setAllSelected] = useState<boolean>(false);

  const checkIfSelected = useCallback(
    (id: string): boolean => itemsSelected.includes(id),
    [itemsSelected],
  );

  const toggleRowSelect = useCallback(
    (id: string): void => {
      if (checkIfSelected(id)) {
        setItemsSelected(itemsSelected.filter(item => item !== id));
        return;
      }

      setItemsSelected([...itemsSelected, id]);
    },
    [itemsSelected, checkIfSelected],
  );

  const selectAllItemsOnPage = useCallback(
    (ids: Array<string>, shouldBeSelected: boolean): void => {
      if (shouldBeSelected) {
        setItemsSelected([...itemsSelected, ...ids]);
        return;
      }

      setItemsSelected(itemsSelected.filter(item => !ids.includes(item)));
    },
    [itemsSelected],
  );

  const selectAllItems = useCallback(
    (ids: Array<string>): void => {
      if (allSelected) {
        setItemsSelected([]);
        setAllSelected(false);
        return;
      }

      const newArr = [...itemsSelected, ...ids];
      const duplicatesRemoved = deduplicated(newArr, id => id);
      setItemsSelected(duplicatesRemoved);
      setAllSelected(true);
    },
    [allSelected, itemsSelected],
  );

  const deselectAllItems = (): void => {
    setItemsSelected([]);
    setAllSelected(false);
  };

  return {
    itemsSelected,
    allSelected,
    selectAllItemsOnPage,
    toggleRowSelect,
    checkIfSelected,
    selectAllItems,
    deselectAllItems,
  };
};

export default useRowSelect;
